import React, {useEffect} from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {LocalizationProvider, DatePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CustomSwitch from '../../../custom/customSwitch';
import franchises from '../franchises';
import dayjs from "dayjs";
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';

export default function FamilyMembers({familyMembers, onChange}) {

    useEffect(() => {
        const updatedMembers = familyMembers.map(member => {
            let dob = member.dateOfBirth;
            let defaultFranchise = 0;

            if (!dayjs(dob).isValid()) {
                member.dateOfBirth = dayjs();
                dob = dayjs();
            }

            if (dob && dayjs(dob).isValid()) {
                const age = dayjs().diff(dayjs(dob), 'year');
                defaultFranchise = age < 18 ? 0 : 300;
            }

            if (member.franchise !== defaultFranchise) {
                return {...member, franchise: defaultFranchise};
            }
            return member;
        });

        onChange(updatedMembers);
    }, [familyMembers.map(m => m.dateOfBirth).join('|')]);

    const handleChange = (index, field, value) => {

        const updated = familyMembers.map((member, i) => {

            if (i !== index) return member;

            if (field === 'additionalInsurancePackage') {
                return {
                    ...member,
                    additionalInsurancePackage: value,
                    additionalInsuranceCustomOption:
                        value === 'custom' ? ['hospital'] : []
                };
            }

            return {...member, [field]: value};
        });

        onChange(updated);
    };

    const handleAddMember = () => {
        onChange([
            ...familyMembers,
            {
                firstName: '',
                lastName: '',
                dateOfBirth: dayjs(),
                gender: 'male',
                franchise: franchises[0].uid,
                accidentsCoverage: true,

                additionalInsurancePackage: '',
                additionalInsuranceCustomOption: []
            },
        ]);
    };

    const handleRemoveMember = (index) => {
        const updated = familyMembers.filter((_, i) => i !== index);
        onChange(updated);
    };

    const normalizeDOB = (dob) => {
        const date = dayjs(dob, 'DD-MM-YYYY');
        if (date.isValid()) {
            return date;
        } else {
            return dayjs();
        }
    };

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>
                Familienmitglieder
            </Typography>

            {familyMembers.map((member, index) => (
                <Box
                    key={index}
                    sx={{
                        border: '2px dotted #999',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        position: 'relative',
                    }}
                >
                    <IconButton
                        color="error"
                        onClick={() => handleRemoveMember(index)}
                        sx={{position: 'absolute', top: 1, right: 1}}
                    >
                        <CloseIcon/>
                    </IconButton>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Vorname"
                                fullWidth
                                variant="standard"
                                required
                                value={member.firstName}
                                onChange={(e) =>
                                    handleChange(index, 'firstName', e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nachname"
                                variant="standard"
                                fullWidth
                                required
                                value={member.lastName}
                                onChange={(e) =>
                                    handleChange(index, 'lastName', e.target.value)
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} style={{minWidth: '170px'}}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel>Geschlecht</InputLabel>
                                <Select
                                    value={member.gender ? member.gender : 'male'}
                                    name="gender"
                                    label="Geschlecht"
                                    onChange={(e) =>
                                        handleChange(index, 'gender', e.target.value)
                                    }
                                >
                                    <MenuItem value="male">
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <MaleIcon fontSize="small"/>
                                            <Box sx={{ml: 1}}>Männlich</Box>
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="female">
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <FemaleIcon fontSize="small"/>
                                            <Box sx={{ml: 1}}>Weiblich</Box>
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="pregnant">
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <PregnantWomanIcon fontSize="small"/>
                                            <Box sx={{ml: 1}}>Schwanger</Box>
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Geburtsdatum"
                                    name="dob"
                                    value={normalizeDOB(member.dateOfBirth)}
                                    onChange={(newValue) =>
                                        handleChange(index, 'dateOfBirth', newValue)
                                    }
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>

                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                variant="standard"
                                label="Franchise"
                                fullWidth
                                required
                                sx={{minWidth: '100px'}}
                                value={member.franchise || 0}
                                onChange={(e) =>
                                    handleChange(index, 'franchise', e.target.value)
                                }
                            >
                                {franchises.map((option) => (
                                    <MenuItem key={option.uid} value={option.label}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box sx={{display: 'flex', alignItems: 'center', mt: 2}}>
                                <Typography variant="body1" sx={{mr: 2, width: 150}}>
                                    Unfälle einschließen:
                                </Typography>
                                <CustomSwitch
                                    checked={member.accidentsCoverage}
                                    onChange={(e) =>
                                        handleChange(index, 'accidentsCoverage', e.target.checked)
                                    }
                                    name="Include Accidents"
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{mt: 2}}>

                        <Grid item xs={12} sm={6}>
                            <FormControl variant="standard" sx={{minWidth: 175, maxWidth: 400}}>
                                <InputLabel>Paket auswählen</InputLabel>
                                <Select
                                    value={member.additionalInsurancePackage || ''}
                                    onChange={(e) =>
                                        handleChange(index, 'additionalInsurancePackage', e.target.value)
                                    }
                                >
                                    <MenuItem value="solid_basic">Bronze</MenuItem>
                                    <MenuItem value="best_value">Silber</MenuItem>
                                    <MenuItem value="premium">Gold</MenuItem>
                                    <MenuItem value="custom">Individuell</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {member.additionalInsurancePackage === 'custom' && (
                            <Grid item xs={12} sm={6}>
                                <FormControl variant="standard" sx={{
                                    width: 365,
                                }}>
                                    <InputLabel>Zusatzleistungen</InputLabel>

                                    <Select
                                        multiple
                                        value={member.additionalInsuranceCustomOption || []}
                                        onChange={(e) =>
                                            handleChange(
                                                index,
                                                'additionalInsuranceCustomOption',
                                                typeof e.target.value === 'string'
                                                    ? e.target.value.split(',')
                                                    : e.target.value
                                            )
                                        }
                                        renderValue={(selected) => selected.join(', ')}
                                    >
                                        <MenuItem value="hospital">Krankenhaus</MenuItem>
                                        <MenuItem value="ambulance">Krankenwagen</MenuItem>
                                        <MenuItem value="medicines">Medikamente</MenuItem>
                                        <MenuItem value="naturopathy">Naturheilkunde</MenuItem>
                                        <MenuItem value="spa">Spa</MenuItem>
                                        <MenuItem value="fitness">Fitness</MenuItem>
                                        <MenuItem value="dental">Zahnbehandlung</MenuItem>
                                        <MenuItem value="eyewear">Brillen</MenuItem>
                                        <MenuItem value="maternity">Mutterschaft</MenuItem>
                                        <MenuItem value="travel">Reiseversicherung</MenuItem>
                                        <MenuItem value="worldwide">Weltweite Deckung</MenuItem>
                                        <MenuItem value="extracare">Erweiterte Pflege</MenuItem>
                                    </Select>

                                </FormControl>
                            </Grid>
                        )}

                    </Grid>

                </Box>
            ))}

            <Button
                variant="outlined"
                startIcon={<AddIcon/>}
                onClick={handleAddMember}
            >
                Mitglied hinzufügen
            </Button>
        </Box>
    );
}